import React, { useCallback, useState } from 'react'
import { Col, Row, Spinner } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { getExternalLink } from '../../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/utils'
import { SyncStatusView } from '../../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'
import { useSaveMeetingMutation } from '../../../../../store/integrations/integrations'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { TEST_SESSION_FILTER } from '../model'
import { TestResultDisplay } from '../TestResultDisplay'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { RoomSession } from '../../../../../models/Room'
import { MeetingDropdown } from '../MeetingDropdown'
import { IntegrationSettings } from '../../AdminSettings/schema'
import { useFormContext } from 'react-hook-form'
import { ContactDropdown } from '../../../../MeetingSummary/CRM/ContactDropdown'
import { SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import '../MappingTester.scss'
import { useGetMeQuery } from '../../../../../store/user/user'
import { useLazyGetAiAgentResultQuery } from '../../../../../store/ai/aiAgentResult'
import { AIAgentType } from '../../../../../models/AIAgent'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { formatDateDDMMYYYY, formatDateHM, getUserDisplayName } from '../../../../../apps/professional-app/fa/common/aifeatures/utils'
import { parseMetadata } from '../../../../../common/util'

const DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION = 'note'
const CRM_SESSION_OBJECT: Record<string, string> = {
  wealthbox: 'events',
  redtail: 'activities',
  salesforce: 'Event',
  practifi: 'Event',
  xlr8: 'Event',
  zoho: 'Events',
  elements: 'Event',
  blackdiamond: 'Event',
}

export interface MeetingSummaryMappingTesterProps {
  serviceId: IntegrationServiceId
}

export const MeetingSummaryMappingTester: React.FC<MeetingSummaryMappingTesterProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { data: account } = useGetAccountQuery({ accountId })

  const {
    formState: { isDirty, isValid },
    watch,
  } = useFormContext<IntegrationSettings>()
  const contactType = watch('mappings.contact.objectName')

  const isFormUnsaved = isDirty || !isValid
  const integrationName = getIntegrationDisplayName(serviceId)

  const [rawResult, setRawResult] = useState<unknown>(null)
  const [externalLink, setExternalLink] = useState<string | null>(null)
  const [saveMeeting, { isLoading: savingMeeting, isSuccess: saveSuccess, isError: saveIsError }] = useSaveMeetingMutation()
  const [triggerGetAiAgentResult, { isLoading: isGetAiAgentResultLoading }] = useLazyGetAiAgentResultQuery()
  const [selectedContactExternalId, setSelectedContactExternalId] = useState<string | null>(null)
  const [selectedSession, setSelectedSession] = useState<RoomSession | null>(null)

  const onSessionSelected = useCallback((session: RoomSession) => {
    setSelectedSession(session)
  }, [])

  const handleRunTest = useCallback(async () => {
    if (!selectedSession || !selectedContactExternalId || !currentUser) {
      return
    }
    setRawResult(null)
    setExternalLink(null)

    try {
      const selectedMeetingTemplateId = parseMetadata(selectedSession.metadata)?.external?.meetingTemplateId

      const existingMeetingSummary = await triggerGetAiAgentResult(
        {
          accountId,
          type: AIAgentType.MEETING_SUMMARY,
          resultKey: `${selectedSession.roomId}|${selectedSession.id}|${selectedMeetingTemplateId}`,
        },
        false,
      ).unwrap()

      const meetingTitle = parseMetadata(selectedSession.metadata)?.external?.title ?? 'Untitled Meeting'
      const meetingInfoHTML = `<p>
        <strong>${meetingTitle}</strong>
        <p>${formatDateDDMMYYYY(selectedSession.startedAt)}</p>
        <p>${formatDateHM(selectedSession.startedAt)} - ${selectedSession.finishedAt ? formatDateHM(selectedSession.finishedAt) : ''}</p>
        <p>Participants:
        <ul>
        ${selectedSession.users.map((u) => `<li>${getUserDisplayName(u)}</li>`).join('')}
        </ul>
        </p>
      </p>`

      const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
      const accountConfigMappingSession = serviceIntegration
        ? typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
          ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
          : (serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.objectName ?? DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION)
        : DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION

      const originalDescription = (existingMeetingSummary?.result?.summary || '').replace(/@embed\(\w+\([\s\S]*?\)\)/g, '')
      const description =
        accountConfigMappingSession === CRM_SESSION_OBJECT[serviceId] ? originalDescription : `<div>${meetingInfoHTML} ${originalDescription}</div>`

      const result = await saveMeeting({
        accountId,
        connector: serviceId,
        body: {
          roomId: selectedSession.roomId,
          sessionId: selectedSession.id,
          description,
          externalUserIds: [selectedContactExternalId],
          relatedTo: { id: selectedContactExternalId, type: contactType },
          exportType: 'export',
          saveSyncStatus: false,
        },
      }).unwrap()

      setRawResult(result)
      const syncStatus = (result as { data?: SyncStatusView[] } | undefined)?.data?.[0]
      if (syncStatus) {
        setExternalLink(getExternalLink({ syncStatus, currentUser: currentUser }) || null)
      }
    } catch (err) {
      setRawResult(err)
    }
  }, [selectedSession, selectedContactExternalId, accountId, serviceId, saveMeeting, currentUser, contactType, triggerGetAiAgentResult, account])

  const onContactOptionSelected = useCallback((option: SelectOption) => {
    setSelectedContactExternalId(option.id)
  }, [])

  return (
    <>
      <Row className='mb-4 mt-2 align-items-center'>
        <Col>Contact</Col>
        <Col>
          <ContactDropdown
            serviceId={serviceId}
            onContactOptionSelected={onContactOptionSelected}
            initialSelectedOptionExternalId={selectedContactExternalId ?? undefined}
          />
        </Col>
      </Row>
      <Row>
        <Col>Meeting</Col>
        <Col>
          <MeetingDropdown sessionFilter={TEST_SESSION_FILTER} onSessionSelected={onSessionSelected} />
        </Col>
      </Row>

      <div className='border border-dark rounded-3 p-4 mt-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='bi bi-sliders2 fs-5'></i>
              <h6 className='fw-bold mb-0'>Save Meeting Summary Test</h6>
            </div>
            <p className='text-secondary mb-0 small'>Verify that Meeting Summaries are saved to your {integrationName} as configured.</p>
          </div>
          <Tooltip id='meeting-run-test-tooltip' title={isFormUnsaved ? 'Save your changes before running a test' : undefined}>
            <Button
              type='button'
              variant='primaryGhost'
              className='d-flex align-items-center gap-2 fw-semibold flex-shrink-0'
              onClick={handleRunTest}
              disabled={savingMeeting || !selectedSession || !selectedContactExternalId || isFormUnsaved || !currentUser || isGetAiAgentResultLoading}
            >
              {savingMeeting || isGetAiAgentResultLoading ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-play-fill' />}
              Run Test
            </Button>
          </Tooltip>
        </div>

        <TestResultDisplay
          isLoading={savingMeeting || isGetAiAgentResultLoading}
          loadingText='Saving Meeting Summary'
          hasResult={saveSuccess || saveIsError}
          isSuccess={saveSuccess}
          isError={saveIsError}
          integrationName={integrationName}
          rawResult={rawResult}
          successContent={
            <>
              <p className='mb-3 text-dark ps-4'>The meeting summary was saved to your {integrationName} as configured</p>
              <div className='ps-4'>
                {externalLink ? (
                  <a href={externalLink} target='_blank' rel='noreferrer' className='btn btn-outline-dark btn-sm'>
                    View in {integrationName}
                  </a>
                ) : (
                  <button className='btn btn-outline-dark btn-sm' disabled>
                    View in {integrationName}
                  </button>
                )}
              </div>
            </>
          }
          errorContent={<p className='mb-3 text-dark ps-4'>Failed to export the meeting to {integrationName}. Please verify your integration settings.</p>}
        />
      </div>
    </>
  )
}
